import React, { useEffect, useRef, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { Milestone, MilestoneStatus, Phase, Project } from '../../../types';
import {
  getProjectMilestones, getAllMilestones, createMilestoneWithFields, updateMilestone, deleteMilestone, getProjectPhases,
} from '../../../services/milestoneService';
import {
  listAllProjectDependencies, createProjectDependency, deleteProjectDependency,
  ProjectDependency, ProjectDependencyType,
} from '../../../services/projectDependencyService';
import { listProjects } from '../../../services/projectService';
import { isPortfolioManagerOrAbove } from '../../../services/portfolioRoleService';
import { ApprovalEntityType } from '../../../services/approvalService';
import { ActivityEntityType } from '../../../services/activityService';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import EntityApprovalSection from '../../../components/approvals/EntityApprovalSection';
import ModuleGuard from '../../../components/ModuleGuard';
import Badge from '../../../components/Badge';
import FilterPills from '../../../components/FilterPills';
import PeoplePicker, { Person } from '../../../components/PeoplePicker';
import BoardModal, {
  BMCancelButton, BMPrimaryButton, BMDangerButton, BM_LABEL, BM_INPUT, BM_TEXTAREA, BM_TITLE_INPUT,
} from '../../../components/BoardModal';

interface Props {
  pca: IPublicClientApplication;
  projectId: string;
  project: Project;
  /** When set (Teams slip / milestone-approval deep link), open this
   *  milestone's drawer once the list has loaded. */
  focusMilestoneId?: string | null;
  onFocusConsumed?: () => void;
}

const STAT_LABEL: Record<number, string> = {
  100000000: 'Not Started', 100000001: 'In Progress', 100000002: 'Complete', 100000003: 'At Risk', 100000004: 'Missed',
};
const STAT_BADGE: Record<number, { bg: string; fg: string }> = {
  100000000: { bg: '#F3F4F6', fg: '#5A6272' },
  100000001: { bg: '#D4EEF5', fg: '#0A5568' },
  100000002: { bg: '#D1FAE5', fg: '#065F46' },
  100000003: { bg: '#FEF3C7', fg: '#92400E' },
  100000004: { bg: '#FEE2E2', fg: '#991B1B' },
};

type StatusFilter = 'all' | 100000000 | 100000001 | 100000002 | 100000003 | 100000004;
const FILTER_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'all',     label: 'All' },
  { value: 100000000, label: 'Not Started' },
  { value: 100000001, label: 'In Progress' },
  { value: 100000002, label: 'Complete' },
  { value: 100000003, label: 'At Risk' },
  { value: 100000004, label: 'Missed' },
];

export default function MilestonesTab(props: Props) {
  return (
    <ModuleGuard project={props.project} flag="dagrun_pm_module_milestones">
      <MilestonesTabBody {...props} />
    </ModuleGuard>
  );
}

function MilestonesTabBody({ pca, projectId, focusMilestoneId, onFocusConsumed }: Props) {
  const [items, setItems]       = useState<Milestone[]>([]);
  const [phases, setPhases]     = useState<Phase[]>([]);
  const [filter, setFilter]     = useState<StatusFilter>('all');
  const [selected, setSelected] = useState<Milestone | null>(null);
  const [showNew, setShowNew]   = useState(false);

  const reload = async () => {
    const [m, p] = await Promise.all([
      getProjectMilestones(pca, projectId),
      getProjectPhases(pca, projectId).catch(() => [] as Phase[]),
    ]);
    setItems(m); setPhases(p);
  };
  useEffect(() => { reload(); }, [pca, projectId]); // eslint-disable-line

  // Deep-link focus: once milestones are loaded, open the targeted one's
  // drawer (which hosts its approval section). One-shot — cleared via
  // onFocusConsumed so re-renders don't reopen it after the user closes it.
  useEffect(() => {
    if (!focusMilestoneId || items.length === 0) return;
    const target = items.find(m => m.dagrun_pm_milestoneid === focusMilestoneId);
    if (target) setSelected(target);
    onFocusConsumed?.();
  }, [focusMilestoneId, items]); // eslint-disable-line react-hooks/exhaustive-deps

  const phaseNameById: Record<string, string> = {};
  phases.forEach(p => { phaseNameById[p.dagrun_pm_phaseid] = p.dagrun_pm_name; });

  const filtered = filter === 'all' ? items : items.filter(m => m.dagrun_pm_status === filter);

  return (
    <div style={{ padding: '20px 32px', fontFamily: 'var(--font-ui)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: '#0F1117' }}>Milestones</h3>
        <button type="button" onClick={() => setShowNew(true)}
          style={{ background: '#0E7490', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 6, fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
          onMouseEnter={e => (e.currentTarget.style.background = '#0A5568')}
          onMouseLeave={e => (e.currentTarget.style.background = '#0E7490')}
        >+ Add Milestone</button>
      </div>

      <div style={{ marginBottom: 14 }}>
        <FilterPills value={filter} options={FILTER_OPTIONS} onChange={setFilter} />
      </div>

      {filtered.length === 0 ? (
        <div style={{ padding: 48, textAlign: 'center', color: '#9198A8', fontSize: 14 }}>No milestones match this filter.</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr style={{ borderBottom: '1px solid #DDE0E6', textAlign: 'left' }}>
            <Th>Title</Th><Th>Due Date</Th><Th>Phase</Th><Th>Owner</Th><Th>Status</Th><Th>Client</Th>
          </tr></thead>
          <tbody>
            {filtered.map(m => (
              <tr key={m.dagrun_pm_milestoneid} onClick={() => setSelected(m)}
                style={{ borderBottom: '0.5px solid #EAECF0', cursor: 'pointer', transition: 'background 0.12s' }}
                onMouseEnter={e => (e.currentTarget.style.background = '#F5F6F8')}
                onMouseLeave={e => (e.currentTarget.style.background = '')}>
                <Td><span style={{ color: '#0F1117', fontSize: 14 }}>{m.dagrun_pm_name}</span></Td>
                <Td><span style={{ fontSize: 12, color: '#5A6272', fontFamily: 'var(--font-mono)' }}>{m.dagrun_pm_duedate ? new Date(m.dagrun_pm_duedate).toLocaleDateString() : '—'}</span></Td>
                <Td><span style={{ fontSize: 13, color: '#5A6272' }}>{m._dagrun_pm_phaseid_value ? (phaseNameById[m._dagrun_pm_phaseid_value] ?? '—') : '—'}</span></Td>
                <Td onClick={e => e.stopPropagation()}>
                  <InlineOwnerPicker
                    pca={pca}
                    current={m.dagrun_pm_ownername ?? null}
                    currentId={m.dagrun_pm_ownerid ?? null}
                    onChange={async (p) => {
                      await updateMilestone(pca, m.dagrun_pm_milestoneid, {
                        dagrun_pm_ownerid: p?.aadObjectId ?? null,
                        dagrun_pm_ownername: p?.displayName ?? null,
                      });
                      setItems(prev => prev.map(x => x.dagrun_pm_milestoneid === m.dagrun_pm_milestoneid
                        ? { ...x, dagrun_pm_ownerid: p?.aadObjectId ?? null, dagrun_pm_ownername: p?.displayName ?? null }
                        : x));
                    }}
                  />
                </Td>
                <Td><Badge {...STAT_BADGE[m.dagrun_pm_status]}>{STAT_LABEL[m.dagrun_pm_status]}</Badge></Td>
                <Td>{m.dagrun_pm_isclientvisible ? <span style={{ color: '#059669' }}>✓</span> : <span style={{ color: '#B8BEC9' }}>—</span>}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showNew && (
        <CreateMilestoneModal pca={pca} projectId={projectId} phases={phases} onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); reload(); }} />
      )}
      {selected && (
        <MilestoneDrawer pca={pca} projectId={projectId} milestone={selected} phases={phases}
          onClose={() => { setSelected(null); reload(); }}
          onDeleted={() => { setSelected(null); reload(); }}
        />
      )}
    </div>
  );
}

const Th = ({ children }: { children: React.ReactNode }) => (
  <th style={{ padding: '8px 12px', fontWeight: 500, fontSize: 12, color: '#5A6272', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{children}</th>
);
const Td = ({ children, onClick }: { children: React.ReactNode; onClick?: (e: React.MouseEvent) => void }) => (
  <td onClick={onClick} style={{ padding: '10px 12px', verticalAlign: 'middle' }}>{children}</td>
);

// Inline owner picker for the milestones table. Click the cell → PeoplePicker
// popover. Selection calls the onChange handler. Clicking anywhere outside
// closes the popover without saving.
function InlineOwnerPicker({ pca, current, currentId, onChange }: {
  pca: IPublicClientApplication;
  current: string | null;
  currentId: string | null;
  onChange: (p: Person | null) => Promise<void> | void;
}) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  return (
    <div ref={wrap} style={{ position: 'relative', minWidth: 120 }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{
          background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
          fontSize: 13, color: current ? '#5A6272' : '#B8BEC9',
          fontFamily: 'var(--font-ui)', fontStyle: current ? 'normal' : 'italic',
        }}
      >{current ?? 'Unassigned'}</button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, marginTop: 4,
          background: '#fff', border: '1px solid #DDE0E6', borderRadius: 6,
          boxShadow: '0 4px 16px rgba(15,17,23,0.12)',
          padding: 8, zIndex: 50, minWidth: 260,
          maxWidth: 'calc(100vw - 32px)',
        }}>
          <PeoplePicker
            pca={pca}
            value={currentId && current ? { aadObjectId: currentId, displayName: current } : null}
            onChange={async (p) => {
              await onChange(p);
              setOpen(false);
            }}
            placeholder="Select owner…"
          />
        </div>
      )}
    </div>
  );
}

function CreateMilestoneModal({ pca, projectId, phases, onClose, onCreated }: { pca: IPublicClientApplication; projectId: string; phases: Phase[]; onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState('');
  const [duedate, setDuedate] = useState('');
  const [phaseid, setPhaseid] = useState('');
  const [owner, setOwner] = useState<Person | null>(null);
  const [criteria, setCriteria] = useState('');
  const [clientVisible, setClientVisible] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!name.trim() || saving) return;
    setSaving(true); setError(null);
    try {
      await createMilestoneWithFields(pca, projectId, {
        name: name.trim(),
        duedate: duedate || null,
        phaseid: phaseid || null,
        completioncriteria: criteria.trim() || null,
        isclientvisible: clientVisible,
        ownerid: owner?.aadObjectId ?? null,
        ownername: owner?.displayName ?? null,
      });
      onCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Create failed');
    } finally { setSaving(false); }
  };

  return (
    <BoardModal open title="Add Milestone" onClose={onClose}
      footer={<>
        <BMCancelButton onClick={onClose} disabled={saving} />
        <BMPrimaryButton onClick={submit} disabled={saving || !name.trim()}>{saving ? 'Creating…' : 'Add Milestone'}</BMPrimaryButton>
      </>}
    >
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Title *</label>
        <input autoFocus value={name} onChange={e => setName(e.target.value)} style={BM_TITLE_INPUT} placeholder="Short, specific milestone title" />
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Due Date</label>
        <input type="date" value={duedate} onChange={e => setDuedate(e.target.value)} style={BM_INPUT} />
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Phase</label>
        <select value={phaseid} onChange={e => setPhaseid(e.target.value)} style={BM_INPUT}>
          <option value="">No phase</option>
          {phases.map(p => <option key={p.dagrun_pm_phaseid} value={p.dagrun_pm_phaseid}>{p.dagrun_pm_name}</option>)}
        </select>
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Owner</label>
        <PeoplePicker pca={pca} value={owner} onChange={setOwner} placeholder="Select owner…" />
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Completion Criteria</label>
        <textarea value={criteria} onChange={e => setCriteria(e.target.value)} style={BM_TEXTAREA} />
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#5A6272', cursor: 'pointer' }}>
        <input type="checkbox" checked={clientVisible} onChange={e => setClientVisible(e.target.checked)} />
        Visible to clients
      </label>
      {error && <div style={{ marginTop: 12, color: '#DC2626', fontSize: 12 }}>{error}</div>}
    </BoardModal>
  );
}

function MilestoneDrawer({ pca, projectId, milestone, phases, onClose, onDeleted }: { pca: IPublicClientApplication; projectId: string; milestone: Milestone; phases: Phase[]; onClose: () => void; onDeleted: () => void }) {
  const [draft, setDraft] = useState<Milestone>(milestone);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [open, setOpen] = useState({ details: true, criteria: true, predecessors: true, approvals: false });
  const me = useCurrentUser(pca, projectId);
  const [approvalGate, setApprovalGate] = useState({ hasChain: false, requireCompletion: false, allApproved: false });
  const [statusBlockMsg, setStatusBlockMsg] = useState<string | null>(null);
  const flashTimer = useRef<number | null>(null);
  const textDebounce = useRef<number | null>(null);

  useEffect(() => { setDraft(milestone); }, [milestone]);

  const patch = async (fields: Partial<Milestone>) => {
    setSaving(true);
    try {
      await updateMilestone(pca, draft.dagrun_pm_milestoneid, fields as Record<string, unknown>);
      setSavedFlash(true);
      if (flashTimer.current) window.clearTimeout(flashTimer.current);
      flashTimer.current = window.setTimeout(() => setSavedFlash(false), 1500);
    } finally { setSaving(false); }
  };

  const setAndPatch = <K extends keyof Milestone>(k: K, v: Milestone[K]) => {
    setDraft(d => ({ ...d, [k]: v }));
    patch({ [k]: v } as Partial<Milestone>);
  };

  const setText = <K extends keyof Milestone>(k: K, v: Milestone[K]) => {
    setDraft(d => ({ ...d, [k]: v }));
    if (textDebounce.current) window.clearTimeout(textDebounce.current);
    textDebounce.current = window.setTimeout(() => patch({ [k]: v } as Partial<Milestone>), 600);
  };
  const flush = <K extends keyof Milestone>(k: K, v: Milestone[K]) => {
    if (textDebounce.current) window.clearTimeout(textDebounce.current);
    patch({ [k]: v } as Partial<Milestone>);
  };

  const setPhase = (phaseId: string) => {
    setDraft(d => ({ ...d, _dagrun_pm_phaseid_value: phaseId || null }));
    const fields = phaseId
      ? { 'dagrun_pm_phaseid@odata.bind': `/dagrun_pm_phases(${phaseId})` } as Record<string, unknown>
      : { 'dagrun_pm_phaseid@odata.bind': null } as Record<string, unknown>;
    patch(fields as Partial<Milestone>);
  };

  const isComplete = draft.dagrun_pm_status === MilestoneStatus.Complete;

  const doDelete = async () => {
    setSaving(true);
    try { await deleteMilestone(pca, draft.dagrun_pm_milestoneid); onDeleted(); } finally { setSaving(false); }
  };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,17,23,0.18)', zIndex: 100 }} />
      <div style={{
        position: 'fixed', top: 0, right: 0, height: '100%', width: 480,
        background: '#fff', borderLeft: '0.5px solid #EAECF0',
        boxShadow: '0 4px 24px rgba(15,17,23,0.12)', zIndex: 101,
        overflowY: 'auto', padding: '16px 24px 32px 24px',
        fontFamily: 'var(--font-ui)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <span style={{ fontSize: 11, color: '#9198A8', opacity: savedFlash ? 1 : 0, transition: 'opacity 0.2s' }}>{savedFlash ? 'Saved ✓' : ''}</span>
          <button onClick={onClose} aria-label="Close" style={{ width: 28, height: 28, border: 'none', background: 'transparent', cursor: 'pointer', color: '#5A6272', fontSize: 18 }}>×</button>
        </div>

        <input value={draft.dagrun_pm_name}
          onChange={e => setText('dagrun_pm_name', e.target.value)}
          onBlur={e => flush('dagrun_pm_name', e.target.value)}
          style={{ fontSize: 18, fontWeight: 600, color: '#0F1117', border: 'none', padding: 0, marginBottom: 8, width: '100%', background: 'transparent' }} />

        <select value={draft.dagrun_pm_status}
          onChange={e => {
            const next = Number(e.target.value);
            if (next === MilestoneStatus.Complete && approvalGate.requireCompletion && !approvalGate.allApproved) {
              setStatusBlockMsg('This milestone requires full approval before it can be marked Complete.');
              return;
            }
            setStatusBlockMsg(null);
            setAndPatch('dagrun_pm_status', next);
          }}
          style={{ ...BM_INPUT, width: 'auto', padding: '4px 8px', fontSize: 12, marginBottom: statusBlockMsg ? 4 : 16 }}>
          {[MilestoneStatus.NotStarted, MilestoneStatus.InProgress, MilestoneStatus.Complete, MilestoneStatus.AtRisk, MilestoneStatus.Missed].map(v => (
            <option key={v} value={v}>{STAT_LABEL[v]}</option>
          ))}
        </select>
        {statusBlockMsg && <div style={{ fontSize: 11, color: '#DC2626', marginBottom: 12 }}>{statusBlockMsg}</div>}

        <Section title="Details" open={open.details} onToggle={() => setOpen(s => ({ ...s, details: !s.details }))}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={BM_LABEL}>Due Date</label>
              <input type="date" value={draft.dagrun_pm_duedate?.slice(0, 10) ?? ''}
                onChange={e => setAndPatch('dagrun_pm_duedate', e.target.value || null)} style={BM_INPUT} />
            </div>
            {isComplete && (
              <div>
                <label style={BM_LABEL}>Completed Date</label>
                <input type="date" value={draft.dagrun_pm_completeddate?.slice(0, 10) ?? ''}
                  onChange={e => setAndPatch('dagrun_pm_completeddate', e.target.value || null)} style={BM_INPUT} />
              </div>
            )}
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={BM_LABEL}>Phase</label>
            <select value={draft._dagrun_pm_phaseid_value ?? ''} onChange={e => setPhase(e.target.value)} style={BM_INPUT}>
              <option value="">No phase</option>
              {phases.map(p => <option key={p.dagrun_pm_phaseid} value={p.dagrun_pm_phaseid}>{p.dagrun_pm_name}</option>)}
            </select>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={BM_LABEL}>Owner</label>
            <PeoplePicker
              pca={pca}
              value={draft.dagrun_pm_ownerid
                ? { aadObjectId: draft.dagrun_pm_ownerid, displayName: draft.dagrun_pm_ownername ?? draft.dagrun_pm_ownerid }
                : null}
              onChange={p => {
                const fields = {
                  dagrun_pm_ownerid: p?.aadObjectId ?? null,
                  dagrun_pm_ownername: p?.displayName ?? null,
                };
                setDraft(d => ({ ...d, ...fields }));
                patch(fields);
              }}
              placeholder="Select owner…"
            />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#5A6272', cursor: 'pointer' }}>
            <input type="checkbox" checked={!!draft.dagrun_pm_isclientvisible}
              onChange={e => setAndPatch('dagrun_pm_isclientvisible', e.target.checked)} />
            Visible to clients
          </label>
        </Section>

        <Section title="Completion Criteria" open={open.criteria} onToggle={() => setOpen(s => ({ ...s, criteria: !s.criteria }))}>
          <textarea value={draft.dagrun_pm_completioncriteria ?? ''}
            onChange={e => setText('dagrun_pm_completioncriteria', e.target.value)}
            onBlur={e => flush('dagrun_pm_completioncriteria', e.target.value)}
            style={BM_TEXTAREA} />
        </Section>

        <Section title="Linked Predecessors" open={open.predecessors} onToggle={() => setOpen(s => ({ ...s, predecessors: !s.predecessors }))}>
          <LinkedPredecessorsSection pca={pca} projectId={projectId} milestone={draft} />
        </Section>

        {me && (
          <Section title="Approvals" open={open.approvals} onToggle={() => setOpen(s => ({ ...s, approvals: !s.approvals }))}>
            <EntityApprovalSection
              pca={pca}
              projectId={projectId}
              entityType={ApprovalEntityType.Milestone}
              entityId={draft.dagrun_pm_milestoneid}
              entityTitle={draft.dagrun_pm_name}
              myOid={me.oid}
              canManage={me.isPM}
              activityEntityType={ActivityEntityType.Milestone}
              onChainState={setApprovalGate}
            />
          </Section>
        )}

        <div style={{ marginTop: 28, display: 'flex', justifyContent: 'flex-end' }}>
          <BMDangerButton onClick={() => setConfirmDelete(true)}>Delete Milestone</BMDangerButton>
        </div>
      </div>

      {confirmDelete && (
        <BoardModal open title="Delete Milestone" onClose={() => setConfirmDelete(false)}
          footer={<>
            <BMCancelButton onClick={() => setConfirmDelete(false)} disabled={saving} />
            <BMDangerButton onClick={doDelete} disabled={saving}>{saving ? 'Deleting…' : 'Delete'}</BMDangerButton>
          </>}>
          Delete <strong style={{ color: '#0F1117' }}>{draft.dagrun_pm_name}</strong>? This cannot be undone.
        </BoardModal>
      )}
    </>
  );
}

const DAY_MS = 86400000;
const DEP_TYPE_LABEL: Record<number, string> = {
  [ProjectDependencyType.FinishToStart]: 'Finish → Start',
  [ProjectDependencyType.StartToStart]: 'Start → Start',
  [ProjectDependencyType.FinishToFinish]: 'Finish → Finish',
};

/**
 * Cross-project predecessor links for one milestone. This milestone is the
 * SUCCESSOR; predecessors live in OTHER projects. Portfolio Managers can
 * add/remove; everyone can view. Backed by dagrun_pm_projectdependency.
 */
function LinkedPredecessorsSection({ pca, projectId, milestone }: {
  pca: IPublicClientApplication; projectId: string; milestone: Milestone;
}) {
  const [deps, setDeps] = useState<ProjectDependency[]>([]);
  const [allMs, setAllMs] = useState<Milestone[]>([]);
  const [projectNames, setProjectNames] = useState<Map<string, string>>(new Map());
  const [canManage, setCanManage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  // Add-form state.
  const [predProjectId, setPredProjectId] = useState('');
  const [predMilestoneId, setPredMilestoneId] = useState('');
  const [lag, setLag] = useState('0');
  const [type, setType] = useState<number>(ProjectDependencyType.FinishToStart);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const reload = async () => {
    const [d, ms, projs, mgr] = await Promise.all([
      listAllProjectDependencies(pca).catch(() => [] as ProjectDependency[]),
      getAllMilestones(pca).catch(() => [] as Milestone[]),
      listProjects(pca).catch(() => []),
      isPortfolioManagerOrAbove(pca).catch(() => false),
    ]);
    setDeps(d); setAllMs(ms); setCanManage(mgr);
    setProjectNames(new Map(projs.map(p => [p.dagrun_pm_projectid, p.dagrun_pm_name])));
    setLoading(false);
  };
  useEffect(() => { reload(); }, [pca, milestone.dagrun_pm_milestoneid]); // eslint-disable-line

  const msById = new Map(allMs.map(m => [m.dagrun_pm_milestoneid, m]));
  // Predecessor links = deps whose successor is THIS milestone.
  const links = deps.filter(d => d.dagrun_pm_successormilestoneid === milestone.dagrun_pm_milestoneid);

  // Other projects that actually have milestones (cross-project only).
  const otherProjectIds = Array.from(new Set(
    allMs.map(m => m._dagrun_pm_projectid_value).filter((p): p is string => !!p && p !== projectId),
  ));
  const predMilestones = predProjectId
    ? allMs.filter(m => m._dagrun_pm_projectid_value === predProjectId && m.dagrun_pm_milestoneid !== milestone.dagrun_pm_milestoneid)
    : [];

  const submit = async () => {
    if (!predProjectId || !predMilestoneId || saving) return;
    setSaving(true); setError(null);
    try {
      const pred = msById.get(predMilestoneId);
      const succDue = milestone.dagrun_pm_duedate ? new Date(milestone.dagrun_pm_duedate).getTime() : null;
      const predDue = pred?.dagrun_pm_duedate ? new Date(pred.dagrun_pm_duedate).getTime() : null;
      const lagDays = parseInt(lag, 10) || 0;
      const slip = predDue != null && succDue != null ? predDue + lagDays * DAY_MS > succDue : false;
      const name = `${pred?.dagrun_pm_name ?? 'Predecessor'} → ${milestone.dagrun_pm_name}`.slice(0, 200);
      await createProjectDependency(pca, {
        name,
        predecessorProjectId: predProjectId,
        predecessorMilestoneId: predMilestoneId,
        successorProjectId: projectId,
        successorMilestoneId: milestone.dagrun_pm_milestoneid,
        lagDays, dependencyType: type, slipDetected: slip,
      });
      setAdding(false); setPredProjectId(''); setPredMilestoneId(''); setLag('0'); setType(ProjectDependencyType.FinishToStart);
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Add failed');
    } finally { setSaving(false); }
  };

  const remove = async (id: string) => {
    try { await deleteProjectDependency(pca, id); setDeps(prev => prev.filter(d => d.dagrun_pm_projectdependencyid !== id)); }
    catch { /* ignore */ }
  };

  if (loading) return <div style={{ fontSize: 12, color: 'var(--ink-4)' }}>Loading…</div>;

  return (
    <div>
      {links.length === 0 && <div style={{ fontSize: 13, color: '#9198A8', marginBottom: 8 }}>No cross-project predecessors linked.</div>}
      {links.map(d => {
        const pred = d.dagrun_pm_predecessormilestoneid ? msById.get(d.dagrun_pm_predecessormilestoneid) : null;
        const projName = d.dagrun_pm_predecessorprojectid ? (projectNames.get(d.dagrun_pm_predecessorprojectid) ?? 'Unknown project') : '—';
        return (
          <div key={d.dagrun_pm_projectdependencyid}
            style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 10px', border: '0.5px solid #EAECF0', borderRadius: 6, marginBottom: 6, background: '#FAFBFC' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, color: '#0F1117' }}>{pred?.dagrun_pm_name ?? '(milestone)'}</div>
              <div style={{ fontSize: 11, color: '#5A6272' }}>
                {projName}
                {pred?.dagrun_pm_duedate ? ` · ${new Date(pred.dagrun_pm_duedate).toLocaleDateString()}` : ''}
                {' · '}{DEP_TYPE_LABEL[d.dagrun_pm_dependencytype ?? ProjectDependencyType.FinishToStart]}
                {d.dagrun_pm_lagdays ? ` · lag ${d.dagrun_pm_lagdays}d` : ''}
              </div>
              {d.dagrun_pm_slipdetected && <div style={{ fontSize: 11, color: '#DC2626', marginTop: 2 }}>⚠ Slip detected</div>}
            </div>
            {canManage && (
              <button type="button" onClick={() => remove(d.dagrun_pm_projectdependencyid)} aria-label="Remove"
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#9198A8', fontSize: 16 }}>×</button>
            )}
          </div>
        );
      })}

      {!canManage && (
        <div style={{ fontSize: 11, color: '#9198A8', marginTop: 4 }}>Portfolio Managers can add cross-project predecessors.</div>
      )}

      {canManage && !adding && (
        <button type="button" onClick={() => setAdding(true)}
          style={{ background: 'transparent', color: '#0E7490', border: '1px solid #0E7490', borderRadius: 6, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-ui)', marginTop: 4 }}>
          + Link a predecessor
        </button>
      )}

      {canManage && adding && (
        <div style={{ marginTop: 8, padding: 10, border: '0.5px solid #EAECF0', borderRadius: 6, background: '#F5F6F8' }}>
          <div style={{ marginBottom: 8 }}>
            <label style={BM_LABEL}>Predecessor project</label>
            <select value={predProjectId} onChange={e => { setPredProjectId(e.target.value); setPredMilestoneId(''); }} style={BM_INPUT}>
              <option value="">— Select project —</option>
              {otherProjectIds.map(pid => <option key={pid} value={pid}>{projectNames.get(pid) ?? 'Project'}</option>)}
            </select>
          </div>
          <div style={{ marginBottom: 8 }}>
            <label style={BM_LABEL}>Predecessor milestone</label>
            <select value={predMilestoneId} onChange={e => setPredMilestoneId(e.target.value)} style={BM_INPUT} disabled={!predProjectId}>
              <option value="">— Select milestone —</option>
              {predMilestones.map(m => (
                <option key={m.dagrun_pm_milestoneid} value={m.dagrun_pm_milestoneid}>
                  {m.dagrun_pm_name}{m.dagrun_pm_duedate ? ` (${new Date(m.dagrun_pm_duedate).toLocaleDateString()})` : ''}
                </option>
              ))}
            </select>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
            <div>
              <label style={BM_LABEL}>Lag days</label>
              <input type="number" value={lag} onChange={e => setLag(e.target.value)} style={BM_INPUT} />
            </div>
            <div>
              <label style={BM_LABEL}>Type</label>
              <select value={type} onChange={e => setType(Number(e.target.value))} style={BM_INPUT}>
                <option value={ProjectDependencyType.FinishToStart}>Finish to Start</option>
                <option value={ProjectDependencyType.StartToStart}>Start to Start</option>
                <option value={ProjectDependencyType.FinishToFinish}>Finish to Finish</option>
              </select>
            </div>
          </div>
          {error && <div style={{ color: '#DC2626', fontSize: 12, marginBottom: 8 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <BMCancelButton onClick={() => { setAdding(false); setError(null); }} disabled={saving} />
            <BMPrimaryButton onClick={submit} disabled={saving || !predProjectId || !predMilestoneId}>{saving ? 'Linking…' : 'Link'}</BMPrimaryButton>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, open, onToggle, children }: { title: string; open: boolean; onToggle: () => void; children: React.ReactNode }) {
  return (
    <div>
      <div onClick={onToggle} style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        fontSize: 11, fontWeight: 600, color: '#5A6272',
        textTransform: 'uppercase', letterSpacing: '0.12em',
        cursor: 'pointer', userSelect: 'none',
        padding: '12px 0 0', borderTop: '0.5px solid #EAECF0', marginTop: 16,
      }}>
        <span>{title}</span>
        <span style={{ fontSize: 10, color: '#9198A8' }}>{open ? '▾' : '▸'}</span>
      </div>
      {open && <div style={{ paddingTop: 12 }}>{children}</div>}
    </div>
  );
}
